import {Chart, registerables} from "chart.js";
import annotationPlugin from "chartjs-plugin-annotation";
import {useEffect, useRef} from "react";

const HourlyLineAndBarChart = (props) => {
    const chartRef = useRef(null);
    const chartInstance = useRef(null);

    Chart.register(...registerables);
    Chart.register(annotationPlugin);

    useEffect(() => {
        if (chartRef.current && props.labels && props.bar_data && props.line_data) {
            // Destroy existing chart if it exists
            if (chartInstance.current) {
                chartInstance.current.destroy();
            }

            // Create new chart
            chartInstance.current = new Chart(chartRef.current, {
                options: {
                    responsive: true,
                    maintainAspectRatio: props?.forceAspect !== undefined ? props.forceAspect : false,
                    plugins: {
                        tooltip: {
                            callbacks: {
                                title: function(context) {
                                    const hour = parseInt(context[0].label);
                                    if (hour === 0) return '12 AM';
                                    if (hour < 12) return hour + ' AM';
                                    if (hour === 12) return '12 PM';
                                    return (hour - 12) + ' PM';
                                }
                            }
                        },
                        annotation: {
                            drawTime: 'afterDatasetsDraw',
                            annotations: {
                                line1: {
                                    type: 'line',
                                    display: props.bar_annotation,
                                    borderDash: [8, 4],
                                    borderWidth: 4,
                                    scaleID: "a",
                                    borderColor: `${props?.bar_color}78`,
                                    value: props.bar_annotation_value || 0,
                                    label: {
                                        display: false,
                                        backgroundColor: props?.bar_color,
                                        borderColor: "#000000",
                                        borderWidth: 2,
                                        content: `Alarm Average: ${props.bar_annotation_value || 0}`,
                                    },
                                    enter({element}, event) {
                                        element.label.options.display = true;
                                        return true;
                                    },
                                    leave({element}, event) {
                                        element.label.options.display = false;
                                        return true;
                                    }
                                },
                                line2: {
                                    type: 'line',
                                    display: props.line_annotation,
                                    borderDash: [8, 4],
                                    borderWidth: 4,
                                    scaleID: "b",
                                    borderColor: `${props?.line_color}78`,
                                    value: props.line_annotation_value || 0,
                                    label: {
                                        display: false,
                                        backgroundColor: props?.line_color,
                                        borderColor: "#000000",
                                        borderWidth: 2,
                                        content: `Response Time Average: ${props.line_annotation_value || 0}`,
                                    },
                                    enter({element}, event) {
                                        element.label.options.display = true;
                                        return true;
                                    },
                                    leave({element}, event) {
                                        element.label.options.display = false;
                                        return true;
                                    }
                                }
                            }
                        },
                        legend: {
                            display: true
                        },
                    },
                    scales: {
                        x: {
                            title: {
                                display: props?.x_axis,
                                text: props?.x_axis_title
                            },
                            grid: {
                                display: false
                            },
                            ticks: {
                                callback: function(value, index, ticks) {
                                    const hour = parseInt(value);
                                    if (hour === 0) return '12 AM';
                                    if (hour < 12) return hour + ' AM';
                                    if (hour === 12) return '12 PM';
                                    return (hour - 12) + ' PM';
                                }
                            }
                        },
                        a: {
                            id: "a",
                            position: "left",
                            grid: {
                                display: false
                            },
                            title: {
                                display: props?.bar_axis,
                                text: props?.bar_axis_title,
                                color: props?.bar_color,
                                font: {
                                    size: 14,
                                    weight: "600"
                                }
                            },
                            suggestedMax: 10,
                            suggestedMin: 0,
                            ticks: {
                                callback: function (value) {
                                    return value.toLocaleString()
                                }
                            }
                        },
                        b: {
                            id: "b",
                            position: "right",
                            title: {
                                display: props?.line_axis,
                                text: props?.line_axis_title,
                                color: props?.line_color,
                                font: {
                                    size: 14,
                                    weight: "600"
                                }
                            },
                            grid: {
                                display: false
                            },
                            suggestedMax: 10,
                            suggestedMin: 0,
                            ticks: {
                                callback: function (value) {
                                    return value.toLocaleString()
                                }
                            }
                        }
                    }
                },
                data: {
                    labels: props.labels,
                    datasets: [{
                        type: "bar",
                        label: props?.bar_axis_title,
                        data: props.bar_data,
                        backgroundColor: [props?.bar_color],
                        hoverBackgroundColor: [`${props?.bar_color}78`],
                        borderRadius: 5,
                        yAxisID: "a",
                        order: 2
                    },{
                        type: "line",
                        label: props?.line_axis_title,
                        data: props.line_data,
                        backgroundColor: [props?.line_color],
                        hoverBackgroundColor: [`${props?.line_color}78`],
                        borderColor: [props?.line_color],
                        borderWidth: 3,
                        pointRadius: 5,
                        pointHoverRadius: 9,
                        pointBackgroundColor: props?.line_color,
                        pointBorderColor: "#ffffff",
                        pointBorderWidth: 1,
                        pointHoverBackgroundColor: props?.line_color,
                        pointHoverBorderColor: "#ffffff",
                        pointHoverBorderWidth: 2,
                        tension: 0.4,
                        fill: false,
                        yAxisID: "b",
                        order: 1
                    }]
                }
            });
        }

        // Cleanup function
        return () => {
            if (chartInstance.current) {
                chartInstance.current.destroy();
            }
        };
    }, [props.labels, props.bar_data, props.line_data, props.bar_annotation_value, props.line_annotation_value]);

    return (<canvas ref={chartRef} className={"chart-canvas p-1"}
                   style={props?.style ? props.style : {width: "100%", height: "100%", display: "block"}}>
            </canvas>);
};

export default HourlyLineAndBarChart;